import { Button, LinkButton } from '@cloudflare/kumo';
import { ArrowLeft } from '@phosphor-icons/react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createRoute, Link, useParams, useRouter } from '@tanstack/react-router';
import { useState } from 'react';
import { ownerLabel } from '../../shared/contracts.ts';
import { meQuery, api, queryClient, unwrap } from '../api.ts';
import { ErrorNotice, Loading } from '../common.tsx';
import { Route as parent } from './signed-in.tsx';
export const Route = createRoute({
  getParentRoute: () => parent,
  path: '/transfers/$transferId',
  component: TransferPage,
});

function TransferPage() {
  const router = useRouter();
  const { transferId } = useParams({ strict: false });
  const [authorizeRecurring, setAuthorization] = useState(false);
  const transfer = useQuery({
    queryKey: ['transfer', transferId],
    queryFn: async () =>
      unwrap(await api.GET('/api/transfers/{transferId}', { params: { path: { transferId: transferId! } } })),
    refetchInterval: (query) =>
      query.state.data &&
      ['Preparing', 'AwaitingPaymentMethod', 'AwaitingPayment', 'Committing'].includes(
        query.state.data.status,
      )
        ? 2000
        : false,
  });
  const me = useQuery(meQuery);
  const accountId = transfer.data?.toUserId ?? me.data?.id;
  const billing = useQuery({
    queryKey: ['billing', accountId],
    enabled: Boolean(accountId && transfer.data?.recipient),
    queryFn: async () =>
      unwrap(
        await api.GET('/api/accounts/{accountUserId}/billing', {
          params: { path: { accountUserId: accountId! } },
        }),
      ),
  });
  const changed = () => queryClient.invalidateQueries();
  const accept = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.POST('/api/transfers/{transferId}/acceptance', {
          params: { path: { transferId: transferId! } },
          body: {
            ...(transfer.data!.coverage === 'free'
              ? {}
              : {
                  billing: {
                    authorizeRecurring: true,
                    expectedPrepaidUntil: transfer.data!.prepaidUntil,
                  },
                }),
          },
        }),
      ),
    onSuccess: changed,
  });
  const withdraw = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.POST('/api/transfers/{transferId}/withdrawal', {
          params: { path: { transferId: transferId! } },
          body: {},
        }),
      ),
    onSuccess: changed,
  });
  const refresh = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.POST('/api/transfers/{transferId}/refresh', {
          params: { path: { transferId: transferId! } },
          body: {},
        }),
      ),
    onSuccess: changed,
  });
  if (transfer.isPending) return <Loading />;
  if (transfer.error)
    return (
      <main className="mx-auto my-12 p-6 max-w-168 [&_p]:mx-0 [&_p]:my-4 [&_p]:leading-[1.65] [&_.checkbox-row]:mx-0 [&_.checkbox-row]:my-5 [&_.action-row]:mt-6 [&_.action-row]:flex-wrap">
        <h1 className="text-[30px] leading-[1.2] font-semibold tracking-[-0.8px]">Transfer unavailable</h1>
        <ErrorNotice error={transfer.error} />
        <p className="leading-[1.65]">Sign in with the invited email address to review this transfer.</p>
      </main>
    );
  const info = transfer.data,
    billed = info.coverage !== 'free',
    date = (value: string) =>
      new Date(value).toLocaleString(undefined, { dateStyle: 'long', timeStyle: 'short' });
  const pending = ['Preparing', 'AwaitingPaymentMethod', 'AwaitingPayment', 'Committing'].includes(
    info.status,
  );
  return (
    <main className="mx-auto my-12 p-6 max-w-168 [&_p]:mx-0 [&_p]:my-4 [&_p]:leading-[1.65] [&_.checkbox-row]:mx-0 [&_.checkbox-row]:my-5 [&_.action-row]:mt-6 [&_.action-row]:flex-wrap">
      <Link
        to="/"
        className="back-link inline-flex items-center gap-2 text-kumo-subtle text-[13px] mb-4 [&:hover]:text-kumo-default"
      >
        <ArrowLeft />
        Dashboard
      </Link>
      <p className="mx-0 mt-0 mb-3 leading-[1.65] text-[11px] font-semibold text-kumo-subtle uppercase tracking-[1.5px]">
        Ownership transfer
      </p>
      <h1 className="text-[30px] leading-[1.2] font-semibold tracking-[-0.8px]">{info.siteName}</h1>
      <p className="leading-[1.65]">
        From {ownerLabel({ name: info.fromOwnerName, email: info.fromOwnerEmail })} to {info.toEmail}.
      </p>
      <ErrorNotice
        error={
          accept.error ??
          withdraw.error ??
          refresh.error ??
          (info.recipient ? (billing.error ?? me.error) : null)
        }
      />
      {info.error ? (
        <p className="leading-[1.65]" role="alert">
          {info.error}
        </p>
      ) : null}
      {info.status === 'Accepted' ? (
        <>
          <p className="leading-[1.65]" role="status">
            Ownership transferred.
          </p>
          {info.recipient ? (
            <Link to="/sites/$siteId/pages" params={{ siteId: info.siteId }}>
              Open website
            </Link>
          ) : (
            <p className="leading-[1.65]">The receiving account now owns this site.</p>
          )}
        </>
      ) : info.status === 'Pending' ? (
        <>
          <p className="leading-[1.65]">
            {info.coverage === 'free'
              ? 'Free unpublished draft. Accepting requires no payment method, starts no trial, and creates no subscription.'
              : info.prepaidUntil
                ? `Paid coverage follows the site through ${date(info.prepaidUntil)}. Billing is set up before ownership changes, then $15/month.`
                : 'The receiving account needs trial or paid service ready before ownership changes. The sender’s trial does not follow the site.'}
          </p>
          {info.recipient ? (
            <>
              {billed ? (
                <>
                  {info.coverage === 'activation' && billing.data ? (
                    <p className="leading-[1.65]">
                      {billing.data.stripeSubscriptionStatus === 'Trialing' && billing.data.trialEndsAt
                        ? `Included in your current trial through ${date(billing.data.trialEndsAt)}; it will not restart.`
                        : billing.data.trialEligible
                          ? 'Your account is eligible for a three-day trial, then $15 per month for this site.'
                          : 'Payment is required now. An existing subscription is charged for the rest of its cycle, up to $15 for this site.'}
                    </p>
                  ) : null}
                  <p className="leading-[1.65]">
                    The receiving account needs a payment method. Delegates can add one in Billing.
                  </p>
                  <label className="checkbox-row flex items-center gap-2.5 text-[14px] [&_input]:w-4.5 [&_input]:h-4.5">
                    <input
                      type="checkbox"
                      checked={authorizeRecurring}
                      onChange={(event) => setAuthorization(event.target.checked)}
                    />
                    I authorize $15 per month for this site and any initial payment described above.
                  </label>
                </>
              ) : null}
              <div className="action-row flex gap-2 items-center">
                <Button
                  variant="primary"
                  disabled={
                    accept.isPending ||
                    !accountId ||
                    accountId === info.fromUserId ||
                    (billed && (!billing.data || !authorizeRecurring))
                  }
                  onClick={() => accept.mutate()}
                >
                  {billed ? 'Accept and prepare billing' : 'Accept transfer'}
                </Button>
                <Button variant="ghost" disabled={withdraw.isPending} onClick={() => withdraw.mutate()}>
                  Decline transfer
                </Button>
              </div>
            </>
          ) : (
            <Button disabled={withdraw.isPending} onClick={() => withdraw.mutate()}>
              Withdraw transfer request
            </Button>
          )}
        </>
      ) : pending ? (
        <>
          <p className="leading-[1.65]" role="status">
            {info.status === 'AwaitingPaymentMethod'
              ? 'Waiting for the receiving account’s payment method.'
              : info.status === 'AwaitingPayment'
                ? 'Waiting for the receiving account’s payment to complete.'
                : 'Preparing the receiving account and finishing the ownership transfer.'}
          </p>
          <p className="leading-[1.65]">Ownership stays with the sender until these steps finish.</p>
          <div className="action-row flex gap-2 items-center">
            {info.recipient && info.toUserId ? (
              <LinkButton
                variant="primary"
                href={
                  router.buildLocation({
                    to: '/billing',
                    search: {
                      account: info.toUserId,
                      ...(info.billingOperationId ? { operation: info.billingOperationId } : {}),
                    },
                  }).href
                }
              >
                Continue in Billing
              </LinkButton>
            ) : null}
            <Button disabled={refresh.isPending} onClick={() => refresh.mutate()}>
              Check transfer status
            </Button>
          </div>
        </>
      ) : (
        <p className="leading-[1.65]">
          This transfer is {info.status}. The sender can create a new request if needed.
        </p>
      )}
    </main>
  );
}
